import * as Tooltip from "@radix-ui/react-tooltip";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { Stage } from "../lib/types";
import { stageAbbr, stageTone, FORECAST_LABEL } from "../lib/stages";
import { helpLevelStore, useIsDark } from "../lib/prefs";

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

type Variant = "primary" | "secondary" | "ghost" | "danger" | "accent";

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" }>(
  function Button({ variant = "secondary", size = "md", className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={cx(
          "focus-ring inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "h-8 px-2.5 text-[13px]" : "h-9 px-3.5 text-[14px]",
          variant === "primary" && "bg-primary text-on-primary hover:bg-primary-hover",
          variant === "accent" && "bg-accent text-on-accent hover:brightness-95",
          variant === "secondary" && "border border-line bg-surface text-ink hover:bg-hover",
          variant === "ghost" && "text-ink-2 hover:bg-hover hover:text-ink",
          variant === "danger" && "bg-danger-solid text-on-danger hover:brightness-95",
          className,
        )}
        {...rest}
      />
    );
  },
);

export function Segmented<T extends string>(props: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode; title?: string }[];
  onChange: (v: T) => void;
  size?: "sm" | "md" | "lg";
}) {
  return (
    <div role="radiogroup" aria-label={props.label} className="inline-flex rounded-md border border-line bg-sunken p-0.5">
      {props.options.map((o) => {
        const on = o.value === props.value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={on}
            title={o.title}
            onClick={() => props.onChange(o.value)}
            className={cx(
              "focus-ring rounded-[5px] px-2.5 text-[13px] font-medium transition-colors",
              props.size === "sm" ? "h-7" : props.size === "lg" ? "h-11" : "h-8",
              on ? "bg-surface text-ink shadow-[var(--shadow-1)]" : "text-ink-2 hover:text-ink",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Tip({
  content,
  guide,
  shortcut,
  children,
  side = "top",
}: {
  content: ReactNode;
  /** Extra Cliff-Notes step hint shown when Help Level is set to "guided". */
  guide?: ReactNode;
  /** Optional keyboard shortcut pill shown inside the tooltip. */
  shortcut?: string;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  const helpLevel = helpLevelStore.use();
  if (helpLevel === "off") {
    return <>{children}</>;
  }
  return (
    <Tooltip.Root delayDuration={helpLevel === "guided" ? 120 : 200}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={12}
          data-help-level={helpLevel}
          className="z-[70] max-w-sm rounded-md border border-line bg-raised px-3 py-2 text-[12px] leading-[18px] text-ink shadow-[var(--shadow-2)]"
        >
          <div className="flex items-start justify-between gap-2">
            <div>{content}</div>
            {shortcut ? (
              <kbd className="shrink-0 rounded border border-subtle bg-sunken px-1.5 py-0.5 font-mono text-[10px] font-semibold text-ink-2">
                {shortcut}
              </kbd>
            ) : null}
          </div>
          {helpLevel === "guided" ? (
            <div className="mt-1.5 border-t border-subtle pt-1.5 text-[11px] text-ink-2">
              <span className="font-semibold text-accent">Speed-Learn Tip:</span>{" "}
              {guide ?? "Use the ? menu in the header for the 3-step Cliff-Notes drill or to adjust tooltip level."}
            </div>
          ) : null}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export function StagePill({ stage, total = 14, compact }: { stage: Stage | undefined; total?: number; compact?: boolean }) {
  const dark = useIsDark();
  if (!stage) return <span className="text-ink-3">—</span>;
  const t = stageTone(stage, dark)!;
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px] font-semibold whitespace-nowrap"
      style={{ background: t.solid, color: t.on, boxShadow: dark ? undefined : "inset 0 0 0 1px rgb(20 32 46 / .18)" }}
      title={stage.label}
    >
      <span className="truncate">{compact ? stageAbbr(stage) : stage.label}</span>
      <span className="text-[11px] font-medium opacity-75 tnum">
        {stage.sort}/{total}
      </span>
    </span>
  );
}

export function TierChip({ forecast }: { forecast: string | null }) {
  if (!forecast) return <span className="text-ink-3">—</span>;
  const cls =
    forecast === "PIN"
      ? "bg-primary text-on-primary border-primary"
      : forecast === "MONEYBALL"
        ? "border-navy-500 text-navy-600 dark:border-navy-300 dark:text-navy-300"
        : forecast === "HIGH"
          ? "border-dashed border-navy-600 text-navy-600 dark:border-[#9FB0C6] dark:text-[#9FB0C6]"
          : "border-dotted border-line text-ink-2";
  return <span className={cx("inline-flex h-[22px] items-center rounded-full border px-2.5 text-[12px] font-semibold", cls)}>{FORECAST_LABEL[forecast]}</span>;
}

export function Progress({ value, label }: { value: number | null; label: string }) {
  const v = value === null ? 0 : Math.max(0, value);
  const over = v >= 100;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="relative h-2 w-full overflow-hidden rounded-full bg-sunken"
    >
      <div className={cx("h-full rounded-full", over ? "bg-accent" : "bg-primary")} style={{ width: `${Math.min(100, v)}%` }} />
    </div>
  );
}

export function Card({ title, action, children, className }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("rounded-[10px] border border-subtle bg-surface shadow-[var(--shadow-1)]", className)}>
      {title ? (
        <header className="flex items-center justify-between gap-2 border-b border-subtle px-4 py-2.5">
          <h2 className="eyebrow text-ink-2">{title}</h2>
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span role="status" aria-label={label} className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-line border-t-primary" />
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <div className="font-display text-[16px] font-semibold text-ink">{title}</div>
      {children ? <div className="max-w-md text-ink-2">{children}</div> : null}
    </div>
  );
}

export function Wordmark({ onDark = true }: { onDark?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2.5 select-none" aria-label="LendSure Pipeline">
      <span className="font-display text-[20px] leading-none font-bold tracking-[-0.01em]">
        <span className="text-green-500">Lend</span>
        <span className={onDark ? "text-white" : "text-navy-800 dark:text-white"}>Sure</span>
      </span>
      <span className={cx("h-5 w-px", onDark ? "bg-white/30" : "bg-line")} aria-hidden />
      <span className={cx("font-display text-[14px] font-semibold tracking-[0.02em]", onDark ? "text-white/85" : "text-ink-2")}>Pipeline</span>
    </span>
  );
}
